import React, { useState, useEffect } from "react"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "src/components/ui/dialog"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"

import { useRecordPayment } from "../hooks/useBookings"
import { PAYMENT_MODES, formatMoney } from "../constants"

// Records a receipt against a booking, optionally tied to an installment.
export function RecordPaymentDialog({ open, booking, installment, onClose, onSuccess }) {
  const { toast } = useToast()
  const { mutate, isPending } = useRecordPayment()

  const [amount, setAmount] = useState("")
  const [mode, setMode] = useState("neft")
  const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10))
  const [referenceNo, setReferenceNo] = useState("")

  useEffect(() => {
    if (open) {
      setAmount(installment?.balance ? String(installment.balance) : "")
      setMode("neft")
      setReferenceNo("")
      setPaidOn(new Date().toISOString().slice(0, 10))
    }
  }, [open, installment])

  async function handleSubmit() {
    if (!amount || Number(amount) <= 0) {
      toast({ variant: "destructive", title: "Enter a valid amount" })
      return
    }
    try {
      await mutate(booking.id, {
        amount,
        mode,
        paid_on: paidOn,
        reference_no: referenceNo,
        installment_id: installment?.id,
      })
      toast({ title: "Payment recorded" })
      onSuccess?.()
      onClose()
    } catch (errors) {
      const msg = Array.isArray(errors) ? errors[0] : "Could not record payment"
      toast({ variant: "destructive", title: "Payment failed", description: msg })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
          <DialogDescription>
            {installment
              ? `For "${installment.name}" · balance ${formatMoney(installment.balance)}`
              : "Record a receipt against this booking."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Amount (₹)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
            </div>
            <div className="space-y-1.5">
              <Label>Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Paid on</Label>
              <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Reference no.</Label>
              <Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder="UTR / cheque no." />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isPending}>{isPending ? "Recording…" : "Record payment"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
